import { makeAutoObservable, runInAction } from 'mobx'
import RootStore from './root'
import { PagingSubStore, SingleSubStore } from './subStore'
import {
  LOADING_STATUS,
  SaleList,
  TradeList,
  TradeStatusList,
  HttpReq,
  ResProductDetail,
  FavoriteResult,
  ResultBuyProduct,
  ResultTradeProduct,
} from '../common'

export class MarketStore {
  rootStore: RootStore
  productDetail: ResProductDetail | LOADING_STATUS = LOADING_STATUS.LOADING

  /* 작품 상세 */
  productDetailStore = new SingleSubStore<ResProductDetail>(
    (param?: Record<string, any>) => `/product/${param?.productId}`,
  )

  /* 조각 투자 */
  salePagingStore = new PagingSubStore<SaleList>('/product/sales') // 조각 투자 리스트

  /* 트레이드 */
  tradePagingStore = new PagingSubStore<TradeList>('/product/trades') // 트레이드 리스트
  tradeHistoryDonePagingStore = new PagingSubStore<TradeStatusList>( // 체결
    (param?: Record<string, any>) => `/product/${param?.productId}/trade/purchase`,
  )
  tradeHistoryYetPagingStore = new PagingSubStore<TradeStatusList>( // 미체결
    (param?: Record<string, any>) => `/product/${param?.productId}/trade/orders`,
  )

  /* 거래상태 */
  tradeStatusDonePagingStore = new PagingSubStore<TradeStatusList>('/trade/purchased') // 체결
  tradeStatusYetPagingStore = new PagingSubStore<TradeStatusList>('/trade/orders') // 미체결

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  buyProduct = async (
    saleId: number,
    data: {
      walletId?: number
      count: number
      extraData?: {
        receiptPhoneNumber?: number
      }
    },
  ): Promise<ResultBuyProduct | null> => {
    return await HttpReq<ResultBuyProduct>({
      method: 'POST',
      url: `/product/sale/${saleId}/purchase`,
      data: data,
    })
  }

  tradeProduct = async (
    productId: number,
    data: { cost: number; count: number; orderType: 'SELL' | 'BUY' },
  ): Promise<ResultTradeProduct | null> => {
    const tradeResult = await HttpReq<ResultTradeProduct>({
      method: 'POST',
      url: `/product/${productId}/trade`,
      data: data,
    })
    if (tradeResult) {
      await this.getProductDetail(productId)
    }
    return tradeResult
  }

  getProductDetail = async (productId: number): Promise<ResProductDetail | null> => {
    const detail = await HttpReq<ResProductDetail>({
      method: 'GET',
      url: `/product/${productId}`,
    })
    if (detail == null) {
      runInAction(() => {
        this.productDetail = LOADING_STATUS.FAILED
      })
      return null
    }
    runInAction(() => {
      this.productDetail = detail
    })
    return detail
  }

  getProductTradeHistoryListYet = async (
    productId: number,
    data: Record<string, any>,
  ): Promise<ResProductDetail | null> => {
    return await HttpReq<ResProductDetail>({
      method: 'GET',
      url: `/product/${productId}/trade/orders`,
      data: data,
    })
  }

  /* 트레이드 거래 취소 */
  cancelTrade = async (productId: number, tradeOrderId: number, count: number) => {
    const cancelResult = await HttpReq<boolean>({
      method: 'DELETE',
      url: `/product/${productId}/trade/${tradeOrderId}`,
      data: {
        count: Number(count),
      },
    })
    return cancelResult
  }

  /* 작품 알림 설정하기 */
  favoriteProduct = async (productId: number) => {
    const favoriteResult = await HttpReq<FavoriteResult>({
      method: 'POST',
      url: `/product/${productId}/favorite`,
    })
    return favoriteResult?.favorite
  }

  /* 커뮤니티 좋아요 기능 */
  likeContent = async (productId: number, communityId: number): Promise<boolean | null> => {
    return await HttpReq<boolean>({
      method: 'POST',
      url: `/product/${productId}/post/${communityId}/like`,
    })
  }

  /* 커뮤니티 댓글 삭제 기능 */
  deleteContent = async (productId: number, communityId: number): Promise<boolean | null> => {
    const deleteResult = await HttpReq<boolean>({
      method: 'DELETE',
      url: `/product/${productId}/post/${communityId}`,
    })
    if (!deleteResult) {
      return null
    }
    return deleteResult
  }
}
